import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import { getPublicImageUrl } from "../../helpers/helpers";

export default function EventRequestPopup({ config, onDismiss, onSubmit, submitting, isLoggedIn }) {
  const navigate = useNavigate();
  const [eventName, setEventName] = useState("");
  const [eventLocation, setEventLocation] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [freeText, setFreeText] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [matchedEvents, setMatchedEvents] = useState([]);
  const [searchingEvents, setSearchingEvents] = useState(false);

  const searchMatchingEvents = async () => {
    const nq = eventName.trim();
    const lq = eventLocation.trim();
    if (!nq && !lq) { setMatchedEvents([]); return []; }
    setSearchingEvents(true);
    try {
      let q = supabase
        .from("Events")
        .select("id, name, city, event_image_filename, datetime, venue:Venue(id, name)")
        .gte("datetime", new Date().toISOString())
        .order("datetime", { ascending: true })
        .limit(3);
      const filters = [];
      if (nq) filters.push(`name.ilike.%${nq}%`);
      if (lq) filters.push(`city.ilike.%${lq}%`);
      if (filters.length) q = q.or(filters.join(","));
      const { data } = await q;
      if (data) { setMatchedEvents(data); return data; }
    } catch {} finally { setSearchingEvents(false); }
    return [];
  };

  const handleSubmit = async () => {
    if (!eventName && !eventLocation && !eventDate && !freeText) return;
    const results = await onSubmit({
      event_name: eventName, location: eventLocation, event_date: eventDate,
      free_text: freeText, whatsapp, email: contactEmail,
    });
    if (results !== false) {
      setSubmitted(true);
      const evts = await searchMatchingEvents();
      if (evts.length === 0) setTimeout(onDismiss, 2500);
    }
  };

  const handleEventClick = (id) => { onDismiss(); navigate(`/single-event?eventId=${id}`); };

  return (
    <AnimatePresence>
      <motion.div
        key="popup-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[999999] p-4"
        onClick={(e) => { if (e.target === e.currentTarget) onDismiss(); }}
      >
        <motion.div
          key="popup-card"
          initial={{ scale: 0.92, opacity: 0, y: 30 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.92, opacity: 0, y: 30 }}
          transition={{ type: "spring", duration: 0.4 }}
          className="relative bg-[#17121f] border border-[#26202e] rounded-2xl w-[380px] max-w-[calc(100vw-32px)] max-h-[85vh] overflow-y-auto flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-2xl">
            <div
              className="absolute bottom-0 left-0 right-0 h-[400px]"
              style={{ backgroundImage: "linear-gradient(270deg, #17121f 2.2%, #590fc0 31.5%, #75018c 68%, #17121f 97.2%)" }}
            />
            <div className="absolute inset-0 backdrop-blur-[50px] bg-[rgba(11,4,21,0.5)]" />
          </div>

          <button
            onClick={onDismiss}
            className="absolute top-3 right-3 z-20 text-white/40 hover:text-white transition-colors p-1"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {submitted ? (
            <div className="relative z-10 flex flex-col items-center gap-3 py-8 px-6 text-center w-full">
              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring" }} className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                <svg className="w-6 h-6 text-green-400" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
              </motion.div>
              <p className="text-white text-[16px] font-medium">
                {config.success_message || "Thanks! We'll look into it."}
              </p>
              {matchedEvents.length > 0 && (
                <div className="w-full space-y-2 pt-4 mt-4 border-t border-white/10">
                  <p className="text-purple-300 text-[12px] font-medium text-center">
                    We found these events — is this what you're looking for?
                  </p>
                  {matchedEvents.map((evt) => (
                    <button
                      key={evt.id}
                      onClick={() => handleEventClick(evt.id)}
                      className="w-full flex items-center gap-3 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-purple-500/40 rounded-lg p-2 transition-all text-left"
                    >
                      <div className="w-[48px] h-[48px] rounded-md overflow-hidden flex-shrink-0 bg-white/5">
                        {evt.event_image_filename ? (
                          <img src={getPublicImageUrl(evt.event_image_filename)} alt={evt.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-white/20">
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 6v.75m0 3v.75m0 3v.75m0 3V18m-9-5.25h5.25M7.5 15h3M3.375 5.25c-.621 0-1.125.504-1.125 1.125v3.026a2.999 2.999 0 010 5.198v3.026c0 .621.504 1.125 1.125 1.125h17.25c.621 0 1.125-.504 1.125-1.125v-3.026a2.999 2.999 0 010-5.198V6.375c0-.621-.504-1.125-1.125-1.125H3.375z" />
                            </svg>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-[13px] font-medium truncate">{evt.name}</p>
                        <p className="text-white/40 text-[11px] truncate">{evt.venue?.name ? `${evt.venue.name} - ` : ""}{evt.city}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="relative z-10 w-full pt-8 px-6 pb-0 text-center">
                <h2 className="text-[22px] font-semibold text-white leading-tight">{config.title}</h2>
                {config.subtitle && (
                  <p className="text-white/50 text-[14px] mt-1.5">{config.subtitle}</p>
                )}
              </div>

              <div className="relative z-10 w-full px-6 pt-5 pb-1 space-y-2.5">
                {config.show_event_name && (
                  <input
                    className="w-full bg-white/5 border border-white/10 focus:border-purple-500/60 rounded-lg px-4 py-3 text-white text-[14px] placeholder-white/30 outline-none transition-all"
                    placeholder="Event name"
                    value={eventName}
                    onChange={(e) => setEventName(e.target.value)}
                    onBlur={searchMatchingEvents}
                  />
                )}
                {config.show_location && (
                  <input
                    className="w-full bg-white/5 border border-white/10 focus:border-purple-500/60 rounded-lg px-4 py-3 text-white text-[14px] placeholder-white/30 outline-none transition-all"
                    placeholder="Location"
                    value={eventLocation}
                    onChange={(e) => setEventLocation(e.target.value)}
                    onBlur={searchMatchingEvents}
                  />
                )}
                {searchingEvents && (
                  <div className="flex items-center gap-2 py-1">
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-purple-400" />
                    <p className="text-white/40 text-[12px]">Searching our events...</p>
                  </div>
                )}
                {!searchingEvents && matchedEvents.length > 0 && (
                  <div className="space-y-2 pt-1">
                    <p className="text-purple-300 text-[12px] font-medium">
                      We found these on our page — is this what you're looking for?
                    </p>
                    {matchedEvents.map((evt) => (
                      <button
                        key={evt.id}
                        onClick={() => handleEventClick(evt.id)}
                        className="w-full flex items-center gap-3 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-purple-500/40 rounded-lg p-2 transition-all text-left"
                      >
                        <div className="w-[44px] h-[44px] rounded-md overflow-hidden flex-shrink-0 bg-white/5">
                          {evt.event_image_filename ? (
                            <img src={getPublicImageUrl(evt.event_image_filename)} alt={evt.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-white/20">
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 6v.75m0 3v.75m0 3v.75m0 3V18m-9-5.25h5.25M7.5 15h3M3.375 5.25c-.621 0-1.125.504-1.125 1.125v3.026a2.999 2.999 0 010 5.198v3.026c0 .621.504 1.125 1.125 1.125h17.25c.621 0 1.125-.504 1.125-1.125v-3.026a2.999 2.999 0 010-5.198V6.375c0-.621-.504-1.125-1.125-1.125H3.375z" />
                              </svg>
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-[13px] font-medium truncate">{evt.name}</p>
                          <p className="text-white/40 text-[11px] truncate">{evt.venue?.name ? `${evt.venue.name} - ` : ""}{evt.city}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {config.show_date && (
                  <input
                    type="date"
                    className="w-full bg-white/5 border border-white/10 focus:border-purple-500/60 rounded-lg px-4 py-3 text-white text-[14px] placeholder-white/30 outline-none transition-all"
                    value={eventDate}
                    onChange={(e) => setEventDate(e.target.value)}
                  />
                )}
                {config.show_free_text && (
                  <textarea
                    className="w-full bg-white/5 border border-white/10 focus:border-purple-500/60 rounded-lg px-4 py-3 text-white text-[14px] placeholder-white/30 outline-none transition-all resize-none"
                    rows={2}
                    placeholder={config.free_text_label || "Tell us more"}
                    value={freeText}
                    onChange={(e) => setFreeText(e.target.value)}
                  />
                )}
                {config.show_contact_fields && !isLoggedIn && (
                  <>
                    <p className="text-white/40 text-[11px] pt-1">Get notified when we have it</p>
                    <input
                      className="w-full bg-white/5 border border-white/10 focus:border-green-500/60 rounded-lg px-4 py-3 text-white text-[14px] placeholder-white/30 outline-none transition-all"
                      placeholder="WhatsApp number"
                      type="tel"
                      value={whatsapp}
                      onChange={(e) => setWhatsapp(e.target.value)}
                    />
                    <input
                      className="w-full bg-white/5 border border-white/10 focus:border-purple-500/60 rounded-lg px-4 py-3 text-white text-[14px] placeholder-white/30 outline-none transition-all"
                      placeholder="Email address"
                      type="email"
                      value={contactEmail}
                      onChange={(e) => setContactEmail(e.target.value)}
                    />
                  </>
                )}
              </div>

              <div className="relative z-10 w-full px-6 pt-2 pb-6 space-y-3">
                <button
                  onClick={handleSubmit}
                  disabled={submitting || (!eventName && !eventLocation && !eventDate && !freeText)}
                  className="w-full bg-gradient-to-b from-[#7e25f7] to-[#590fc0] border border-[#7e25f7] text-white font-bold text-[15px] py-3.5 px-6 rounded-xl transition-all duration-200 hover:brightness-110 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100 shadow-lg shadow-purple-600/30 flex items-center justify-center gap-2"
                >
                  {submitting && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />}
                  {config.button_text || "Submit"}
                </button>
                <button
                  onClick={onDismiss}
                  className="w-full text-center text-[13px] text-white/40 hover:text-white/70 transition-colors py-1"
                >
                  No thanks
                </button>
              </div>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
